import React from 'react';
import { LazyMotion } from 'motion/react';
import { RecoilRoot } from 'recoil';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { FaqSearchProvider } from './src/contexts/faqSearchContext';
import { PricingToggleProvider } from './src/contexts/pricingToggleContext';
import { ModalProvider } from './src/molecules/Modal/context/ModalContainerContext';
import './src/styles/layout.scss';
import { DisclaimerProvider } from './src/contexts/DisclaimersContext';
import './static/fonts/fonts.scss';

const features = () =>
  import('./src/utils/motionFeatures').then(res => res.default);

export const shouldUpdateScroll = ({
  prevRouterProps,
  routerProps: { location },
}) => {
  // Prevent scrolling to the top when navigating to the same page
  if (prevRouterProps?.location?.pathname === location.pathname) {
    return false;
  }

  return location.state?.scrollY ? [0, location.state.scrollY] : true;
};

const SCROLL_TIMEOUT_MS = 200;

export const onRouteUpdate = ({ location }) => {
  // Manage scroll position
  // Check for hash first, then scrollY, then fallback to top of page
  if (location.hash) {
    window.setTimeout(() => {
      const targetElement = document.getElementById(
        location.hash.replace('#', '')
      );

      if (targetElement) {
        window.scrollTo({
          left: 0,
          top: targetElement.offsetTop - 50,
          behavior: 'smooth',
        });
      }
    }, SCROLL_TIMEOUT_MS);
  } else if (location.state?.scrollY) {
    window.setTimeout(() => {
      window.scrollTo({
        left: 0,
        top: location.state.scrollY,
        behavior: 'smooth',
      });
    }, 0);
  } else if (window.location?.href !== window.locations?.at(-2)) {
    window.setTimeout(() => {
      window.scrollTo({
        left: 0,
        top: 0,
        behavior: 'instant',
      });
    }, SCROLL_TIMEOUT_MS);
  }

  // Populate locations history
  window.locations = window.locations || [document.referrer];
  if (window.locations[locations.length - 1] !== window.location.href) {
    window.locations.push(window.location.href);
  }
  window.previousPath = window.locations[locations.length - 2];
};

const theme = createTheme();

export const wrapRootElement = ({ element }) => (
  <RecoilRoot>
    <ThemeProvider theme={theme}>
      <LazyMotion features={features} strict>
        <DisclaimerProvider>
          <FaqSearchProvider>
            <PricingToggleProvider>
              <ModalProvider>{element}</ModalProvider>
            </PricingToggleProvider>
          </FaqSearchProvider>
        </DisclaimerProvider>
      </LazyMotion>
    </ThemeProvider>
  </RecoilRoot>
);
